import { TitleCasePipe, DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { SeoMetaData } from 'src/app/model/seo/seo.model';
import { BRAND } from '../../data/brand/brand.data';
import { EMPLOYEES } from '../../data/employee/employee.data';
import { NAVIGATIONS } from '../../data/navigation/navigation.data';
import { pageEventName, pageLabel } from '../../data/analytics/page-event.data';
import { AnalyticsService } from '../analytics/analytics.service';

const SITE_ORIGIN = 'https://codigrate.com';
// og:image / twitter:image must be absolute for social crawlers, point at the
// self-hosted logo on our own origin.
const DEFAULT_OG_IMAGE = SITE_ORIGIN + '/assets/brand/min/logo/brand-logo.min.webp';

@Injectable( {
  providedIn: 'root',
} )
export class SeoService {
  // Readable name of the last in-app page the visitor was on (null until the
  // first tracked navigation), so each page event records where the visit came
  // from: 'HEX to RGB', 'Home', 'Themes'.
  private previousModule: string | null = null;
  // The previous page's slug / product, for the action events' page context.
  private previousKey = '';
  /** how many times each page event has fired in this visit, and when the last page view was */
  private viewCounts: { [ event: string ]: number } = {};
  private lastViewAt = 0;
  /** the URL the server rendered the head for, and the one the router is on or moving to (see keepServerHead) */
  private serverPath = '';
  private routeUrl = '';

  constructor(
    private titleService: Title,
    private metaTagService: Meta,
    private analyticsService: AnalyticsService,
    @Inject( DOCUMENT ) private doc: Document
  ) {
    // taken before any page component can rewrite the URL (a tool filling its own params)
    this.serverPath = SeoService.comparableUrl( doc.location ? doc.location.pathname + doc.location.search : '' );
  }

  getAuthors() {
    let authors = '';

    for ( const employee of EMPLOYEES ) {
      authors += employee.profile.name + ', ';
    }
    return authors.substring( 0, authors.length - 2 );
  }

  setMetaData = ( seo: SeoMetaData ) => {

    try {
      // The server (seo-route-meta.js) already wrote this URL's head; crawlers render the first
      // load, so its title, description, robots, og image and canonical stay as they are
      if ( this.keepServerHead() ) {
        this.updateBreadcrumbJsonLd();
        return;
      }

      this.titleService.setTitle( seo.title );
      this.cleanUpMetaData();

      const description = ( seo.description || '' ).substring( 0, 320 );
      const keywords = seo.keywords.join( ', ' );

      this.metaTagService.addTags( [
        { name: 'keywords', content: keywords },
        { name: 'robots', content: seo.robots.join( ', ' ) },
        { name: 'authors', content: this.getAuthors() },
        { name: 'description', content: description },
        { charset: 'UTF-8' },
      ] );

      // Open Graph
      this.upsertMetaProperty( 'og:title', seo.title );
      this.upsertMetaProperty( 'og:description', description );
      this.upsertMetaProperty( 'og:type', 'website' );
      this.upsertMetaProperty( 'og:site_name', BRAND.name );
      this.upsertMetaProperty( 'og:image', DEFAULT_OG_IMAGE );
      this.upsertMetaProperty( 'og:locale', 'en_US' );

      // Twitter
      this.upsertMetaName( 'twitter:card', 'summary_large_image' );
      this.upsertMetaName( 'twitter:title', seo.title );
      this.upsertMetaName( 'twitter:description', description );
      this.upsertMetaName( 'twitter:image', DEFAULT_OG_IMAGE );
      this.upsertMetaName( 'twitter:site', '@codigrate' );

      // Canonical + OG URL based on the current path
      this.updateCanonicalAndOgUrl();

      // BreadcrumbList JSON-LD reflecting the current route.
      this.updateBreadcrumbJsonLd();
    } catch ( e ) {
      console.warn( 'Unknown error occurred while setting Meta Tags ===> ', e );
    }
  };

  /**
   * True while the visitor is still on the URL the server rendered (html[data-seo="server"]).
   * The first move to another URL drops the mark, and from then on the client writes the head.
   * The router's URL decides, not the address bar: the head is written before the bar changes.
   */
  private keepServerHead(): boolean {
    const root = this.doc.documentElement;
    if ( !root || root.getAttribute( 'data-seo' ) !== 'server' ) {
      return false;
    }
    const here = this.routeUrl || SeoService.comparableUrl( this.doc.location ? this.doc.location.pathname + this.doc.location.search : '' );
    if ( here === this.serverPath ) {
      return true;
    }
    root.removeAttribute( 'data-seo' );
    return false;
  }

  /** '/themes/?platform=vs%20code' and '/themes?platform=vs code' read the same */
  private static comparableUrl( url: string ): string {
    let out = url || '/';
    try {
      out = decodeURIComponent( out.replace( /\+/g, ' ' ) );
    } catch {
      // a malformed escape stays as it is
    }
    const [ path, query ] = out.split( '?' );
    return ( path.replace( /\/+$/, '' ) || '/' ) + ( query ? '?' + query : '' );
  }

  cleanUpMetaData = () => {
    this.metaTagService.removeTag( 'name=keywords' );
    this.metaTagService.removeTag( 'name=robots' );
    this.metaTagService.removeTag( 'name=authors' );
    this.metaTagService.removeTag( 'name=description' );
  };

  seoAndAnalytics( url: string, paramMap: Map<string, string>, log: boolean, queryParams?: { [ key: string ]: string }, updateSeo = true ) {
    const navObj = this.getObjectByUrl( url, paramMap );
    if ( updateSeo ) {
      this.routeUrl = SeoService.comparableUrl( url );
    }
    if ( navObj && navObj.title ) {
      // Query-param routes (/themes?platform, /theme?product, /palette?product)
      // ignore their query params in the base NAVIGATIONS entry, so every variant
      // would share one static title (Bing "duplicate title" / "title too long").
      // Override with a per-variant, in-range title + description that mirrors
      // seo-route-meta.js so the client-rendered head matches the server one.
      const querySeo = this.queryRouteMeta( url, queryParams );

      const seoMetaData = {
        title: querySeo ? querySeo.title : navObj.title,
        description: querySeo ? querySeo.description : navObj.description,
        keywords: querySeo ? querySeo.keywords : navObj.keywords,
        robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
      } as SeoMetaData;
      // a silent navigation (skipLocationChange) is the same page view: it must not rewrite the
      // head, or a tool page's curated title picks up the random value it just filled itself with
      if ( updateSeo ) {
        this.setMetaData( seoMetaData );
      }
      if ( log === true ) {
        // Where this visit came from: the previous in-app page if they
        // navigated internally, otherwise the external entry source derived
        // from document.referrer (direct, google, ...). Lets a page report
        // answer "did they reach /color from home, from blog, or land on it
        // directly". `internalNav` separates in-site hops from fresh entries.
        const internalNav = this.previousModule !== null;
        const from = internalNav ? this.previousModule : this.entrySource();
        // The route's own parameters as custom properties, so a page report can
        // segment by the actual colour / palette / theme / category / slug behind
        // the page, not just the module. Path params first, then query params.
        const params: { [ key: string ]: string } = {};
        paramMap.forEach( ( value, key ) => { params[ key ] = value; } );
        if ( queryParams ) {
          for ( const key of Object.keys( queryParams ) ) {
            params[ key ] = queryParams[ key ];
          }
        }
        // Each important route reports under its OWN event name (see
        // PAGE_EVENTS), so a funnel on the HEX to RGB converter needs no filter
        // and an unimportant route (auth, legal, callbacks) reports nothing at
        // all. The event name IS the page, so no page property is sent with it.
        const event = pageEventName( navObj.url, params );
        if ( event ) {
          // Why the same page shows up ten times for one person (owner, 2026-09-23): the count of
          // this view inside the visit and the gap since the previous page, so a bounce back to
          // home reads as "View Index 4, 3 seconds later" instead of an unexplained repeat. The
          // fragment (a /#services-... anchor) rides along as the section they aimed at.
          const visit = this.visitCounters( event );
          const fragment = this.doc.location ? ( this.doc.location.hash || '' ).replace( '#', '' ) : '';
          this.analyticsService.logEvent( event, {
            ...params,
            from,
            internalNav,
            ...visit,
            ...( fragment ? { section: fragment } : {} ),
          } );
        }
        // The page context action events carry (Cart Item Added): this page and
        // the one before it, each with its slug or product.
        const label = pageLabel( navObj.url, params );
        const key = params[ 'slug' ] || params[ 'product' ] || '';
        this.analyticsService.setPageContext( {
          page: label,
          pageKey: key,
          from: from || '',
          fromKey: internalNav ? this.previousKey : '',
        }, url );
        // Remembered whether or not this page itself reported, so the NEXT
        // page's `from` still names where the visitor actually came from.
        this.previousModule = label;
        this.previousKey = key;
      }
    }
  }

  /**
   * The view's place in the visit: how many times this page has been seen since the tab was
   * opened, and how long since the previous page view. Kept in memory, so a reload starts a new
   * visit, which is exactly the distinction a report wants.
   */
  private visitCounters( event: string ): { viewIndex: number; secondsSincePrevious?: number } {
    const count = ( this.viewCounts[ event ] || 0 ) + 1;
    this.viewCounts[ event ] = count;
    const now = Date.now();
    const since = this.lastViewAt ? Math.round( ( now - this.lastViewAt ) / 1000 ) : undefined;
    this.lastViewAt = now;
    return since === undefined ? { viewIndex: count } : { viewIndex: count, secondsSincePrevious: since };
  }

  /** Source of a fresh (non-internal) entry, from document.referrer: 'direct'
   *  when there is none (typed URL, bookmark, app), our own host (a reload or
   *  deep link with no tracked previous page) also counts as 'direct', a known
   *  search engine by name, otherwise the bare referrer host. */
  private entrySource(): string {
    const ref = this.doc.referrer || '';
    if ( !ref ) {
      return 'direct';
    }
    try {
      const host = new URL( ref ).hostname.replace( /^www\./, '' );
      const selfHost = new URL( SITE_ORIGIN ).hostname.replace( /^www\./, '' );
      if ( host === selfHost ) {
        return 'direct';
      }
      if ( /(^|\.)google\./.test( host ) ) {
        return 'google';
      }
      if ( /(^|\.)bing\./.test( host ) ) {
        return 'bing';
      }
      if ( /duckduckgo\./.test( host ) ) {
        return 'duckduckgo';
      }
      return host;
    } catch {
      return 'direct';
    }
  }

  // Per-platform meta for the /themes?platform=X filtered galleries. Kept in
  // sync with THEME_PLATFORMS / themePlatformSeo() in seo-route-meta.js (the
  // server-side injection). Each slug yields a unique <=60 char title and a
  // 120-160 char description so no two platform galleries share meta.
  private static readonly THEME_PLATFORMS: { [ slug: string ]: { name: string; kind: string; target: string; market: string } } = {
    jetbrains: { name: 'JetBrains', kind: 'Editor', target: 'JetBrains IDEs', market: 'JetBrains Marketplace' },
    vscode: { name: 'VS Code', kind: 'Editor', target: 'Visual Studio Code', market: 'VS Code Marketplace' },
    cursor: { name: 'Cursor', kind: 'Editor', target: 'the Cursor AI editor', market: 'Cursor Marketplace' },
    antigravity: { name: 'Antigravity', kind: 'Editor', target: 'the Antigravity editor', market: 'Antigravity Marketplace' },
    vscodium: { name: 'VS Codium', kind: 'Editor', target: 'VS Codium', market: 'Open VSX Registry' },
    chrome: { name: 'Chrome', kind: 'Browser', target: 'Google Chrome', market: 'Chrome Web Store' },
    firefox: { name: 'Firefox', kind: 'Browser', target: 'Mozilla Firefox', market: 'Firefox Add-ons' },
    slack: { name: 'Slack', kind: 'Workspace', target: 'your Slack sidebar', market: 'Slack' },
    ghostty: { name: 'Ghostty', kind: 'Terminal', target: 'the Ghostty terminal', market: 'Ghostty' },
    iterm2: { name: 'iTerm2', kind: 'Terminal', target: 'the iTerm2 terminal', market: 'iTerm2' },
    'sublime text': { name: 'Sublime Text', kind: 'Editor', target: 'the Sublime Text editor', market: 'Package Control' },
    macos: { name: 'MacOS', kind: 'Terminal', target: 'the MacOS Terminal', market: 'MacOS Terminal' },
    line: { name: 'LINE', kind: 'Chat', target: 'the LINE app', market: 'LINE' },
  };

  /** every ?platform= value the theme page selects -> its label (the listing pages, 2026-09-21) */
  private static readonly THEME_LISTING_PLATFORMS: { [ slug: string ]: string } = {
    'jetbrains': 'JetBrains', 'vs code': 'VS Code', 'cursor': 'Cursor', 'antigravity': 'Antigravity',
    'vs codium': 'VS Codium', 'sublime text': 'Sublime Text', 'macos terminal': 'MacOS Terminal', 'iterm2': 'iTerm2',
    'ghostty': 'Ghostty', 'rhino': 'Rhino', 'chrome': 'Chrome', 'firefox': 'Firefox', 'line': 'LINE', 'slack': 'Slack',
  };
  private static readonly STORE_THEMES = new Set<string>( [ 'ocean', 'etna', 'fuji', 'reynisfjara', 'salda', 'spring', 'london', 'madrid', 'prague', 'sydney', 'vienna', 'dublin' ] );
  private static readonly STORE_PLATFORMS = new Set<string>( [ 'sublime text', 'macos terminal', 'iterm2', 'ghostty', 'slack' ] );
  private static readonly CART_FREE_PLATFORMS = new Set<string>( [ 'sublime text', 'macos terminal', 'iterm2', 'ghostty', 'slack' ] );
  /** where a listing that links out is installed from (the platform's own store), by ?platform= value */
  private static readonly LISTING_STORES: { [ slug: string ]: string } = {
    'jetbrains': 'the JetBrains Marketplace', 'vs code': 'the Visual Studio Marketplace', 'chrome': 'the Chrome Web Store', 'firefox': 'Firefox Add-ons', 'line': 'the LINE Store',
    'cursor': 'the Open VSX registry', 'antigravity': 'the Open VSX registry', 'vs codium': 'the Open VSX registry',
  };
  /** Title-case a URL slug: 'aurora-borealis' -> 'Aurora Borealis'. */
  private titleCaseSlug( slug: string ): string {
    return ( slug || '' ).replace( /[-_]+/g, ' ' ).replace( /\s+/g, ' ' ).trim().replace( /\b\w/g, c => c.toUpperCase() )
      // name particles stay lowercase inside a name: 'rio-de-janeiro' -> 'Rio de Janeiro'
      .split( ' ' ).map( ( w, i, all ) => ( i > 0 && i < all.length - 1 && SeoService.NAME_PARTICLES.has( w ) ? w.toLowerCase() : w ) ).join( ' ' );
  }
  private static readonly NAME_PARTICLES = new Set<string>( [ 'De', 'Da', 'Di', 'Du', 'Van', 'Von' ] );

  /**
   * Per-variant SEO for the query-param routes whose base NAVIGATIONS entry
   * ignores its query params (/themes?platform, /theme?product, /palette?product).
   * Mirrors themePlatformSeo / themeProductSeo / themeListingSeo in seo-route-meta.js
   * so the client head matches the server-injected one. Returns null when nothing route-specific applies.
   */
  private queryRouteMeta(
    url: string,
    queryParams?: { [ key: string ]: string }
  ): { title: string; description: string; keywords: string[] } | null {
    const path = ( url || '' ).split( '?' )[ 0 ].replace( /\/+$/, '' );

    // /themes?platform=X
    if ( /(^|\/)themes$/.test( path ) ) {
      const p = SeoService.THEME_PLATFORMS[ ( queryParams?.platform || '' ).toLowerCase() ];
      if ( !p ) {
        return null;
      }
      return {
        title: `${ p.name } Themes - ${ p.kind } Color Schemes | Codigrate`,
        description: `Premium ${ p.name } themes by Codigrate for ${ p.target }: dark and light color schemes with WCAG contrast, palette previews and copy-ready hex codes.`,
        keywords: [ `${ p.name.toLowerCase() } themes`, p.market.toLowerCase(), 'ide themes', 'color scheme', 'dark theme', 'light theme', 'vibe coding', BRAND.name ],
      };
    }

    // /theme/privacy?platform=jetbrains&product=X
    if ( /(^|\/)theme\/privacy$/.test( path ) ) {
      const product = ( queryParams?.product || '' ).toLowerCase();
      if ( !product || ( queryParams?.platform || '' ).toLowerCase() !== 'jetbrains' ) {
        return null;
      }
      const name = product === 'all-in-one' ? 'All In One Themes' : `${ this.titleCaseSlug( product ) } Theme`;
      return {
        title: `${ name } Privacy Policy - Codigrate`,
        description: `Privacy policy of the ${ name } plugin for JetBrains IDEs by Codigrate: no personal or usage data is collected, stored or transmitted; no analytics, tracking or external services.`,
        keywords: [ `${ name.toLowerCase() } privacy policy`, 'jetbrains plugin privacy', 'no data collection', BRAND.name ],
      };
    }

    // /theme/license?platform=jetbrains&product=X
    if ( /(^|\/)theme\/license$/.test( path ) ) {
      const product = ( queryParams?.product || '' ).toLowerCase();
      if ( !product || ( queryParams?.platform || '' ).toLowerCase() !== 'jetbrains' ) {
        return null;
      }
      const name = product === 'all-in-one' ? 'All In One Themes' : `${ this.titleCaseSlug( product ) } Theme`;
      return {
        title: `${ name } License - Codigrate`,
        description: `Standard EULA of the ${ name } plugin for JetBrains IDEs by Codigrate: license, limits, intellectual property, warranty and liability.`,
        keywords: [ `${ name.toLowerCase() } license`, 'eula', 'jetbrains plugin', BRAND.name ],
      };
    }

    // /theme?product=X (and, since 2026-09-21, /theme?product=X&platform=Y: a listing's own page, the
    // title the theme/content page had; mirrors themeListingSeo in seo-route-meta.js)
    if ( /(^|\/)theme$/.test( path ) ) {
      const product = ( queryParams?.product || '' ).toLowerCase();
      if ( !product ) {
        return null;
      }
      const name = this.titleCaseSlug( product );
      const platform = ( queryParams?.platform || '' ).toLowerCase();
      const label = SeoService.THEME_LISTING_PLATFORMS[ platform ];
      if ( label ) {
        const sold = SeoService.STORE_THEMES.has( product ) && SeoService.STORE_PLATFORMS.has( platform );
        const freeDownload = !SeoService.STORE_THEMES.has( product ) && SeoService.CART_FREE_PLATFORMS.has( platform );
        // Rhino's free file downloads straight from the listing (no cart): the colour file on Windows, the installer app on Mac.
        const rhino = !SeoService.STORE_THEMES.has( product ) && platform === 'rhino';
        return {
          title: `${ name } Theme for ${ label } | Codigrate`,
          description: sold
            ? `Buy the premium Codigrate ${ name } theme for ${ label }: one payment, instant download, step-by-step install guide, screenshot and color palette.`
            : freeDownload
              ? `Download the Codigrate ${ name } theme for ${ label } for free: the theme file, a step-by-step install guide, the screenshot and the full hex palette.`
              : rhino
                ? `Download the Codigrate ${ name } theme for Rhino for free: the Windows color file or the Mac installer app, the screenshot and the full hex palette.`
                : `Get the Codigrate ${ name } theme for ${ label }: preview the screenshot and the palette, then install it from ${ SeoService.LISTING_STORES[ platform ] || 'the ' + label + ' store' } in a few steps.`,
          keywords: [ `${ name.toLowerCase() } ${ label.toLowerCase() } theme`, `${ label.toLowerCase() } theme`, `${ name.toLowerCase() } theme`, ...( sold ? [ `premium ${ label.toLowerCase() } theme`, 'buy theme' ] : [] ), BRAND.name, 'color scheme', 'vibe coding' ],
        };
      }
      return {
        title: `${ name } Theme - Palette & Install Guide | Codigrate`,
        description: `Install the Codigrate ${ name } theme on JetBrains, VS Code, Cursor, Chrome, Slack and Ghostty, then preview its screenshots, color palette and hex codes.`,
        keywords: [ `${ name.toLowerCase() } theme`, BRAND.name, 'ide theme', 'color scheme', 'dark theme', 'light theme', 'vibe coding' ],
      };
    }

    // /color-palette?product=X - the palette detail page
    if ( path === '/palette' && !!queryParams?.product ) {
      const product = ( queryParams?.product || '' ).toLowerCase();
      if ( !product ) {
        return null;
      }
      const name = this.titleCaseSlug( product );
      return {
        title: `${ name } Color Palette - Named Colors & Hex Codes | Codigrate`,
        description: `The ${ name } color palette by Codigrate: curated, named colors with hex codes, previewed in the MacOS color picker and free to download as a native .clr color list.`,
        keywords: [ `${ name.toLowerCase() } color palette`, `${ name.toLowerCase() } palette`, 'named colors', 'hex codes', 'macos color palette', 'clr file', BRAND.name ],
      };
    }

    return null;
  }

  getObjectByUrl = ( url: string, paramMap: Map<string, string> ) => {

    // The query string is not part of the route: '/themes?platform=chrome' has
    // to resolve to the `themes` entry, not to a module literally named
    // 'themes?platform=chrome' - which matched nothing, so every query-param
    // route (/themes, /theme, /palette) silently fell back to
    // the HOME entry and reported itself as the home page.
    const parts = url.split( '?' )[ 0 ].split( '/' );
    const end = parts.length - paramMap.size;
    const moduleName = parts[ end - 1 ];
    // Some routes carry a two-segment url (e.g. 'chrome/color-picker'); fall back
    // to the last two segments so those resolve to their NAVIGATIONS entry too.
    const moduleNameWide = parts.slice( Math.max( 0, end - 2 ), end ).join( '/' );

    // The two-segment match wins: '/store/terms' is the Terms of Sale entry
    // ('store/terms'), not the general 'terms' entry that its last segment
    // also names (kaizen 2026-09-20: the tab read "Terms" on the sale terms).
    const keys = Object.keys( NAVIGATIONS );
    const key = keys.find( ( k: string ) => NAVIGATIONS[ k ].url === moduleNameWide )
      ?? keys.find( ( k: string ) => NAVIGATIONS[ k ].url === moduleName );

    let navObj = { ...NAVIGATIONS[ key ] };

    // an unknown route falls back; the home entry is known and simply has an empty url, so testing
    // the url here threw away the site's own title, description and keywords on '/'
    if ( !key ) {
      navObj = {
        url: '',
        title: BRAND.name + ' - ' + BRAND.motto,
        description: BRAND.name + ` provides UI, UX and software solutions, with software consultancy and SAAS solutions for tech companies.`,
        keywords: [ 'software business', 'software consultancy', 'jetbrains', 'plugins', 'themes', 'web-tools' ],
      };
    }

    paramMap.forEach( ( value, key ) => {
      let postfix = value.replace( new RegExp( '-', 'g' ), ' ' ).toLowerCase();

      if ( key === 'color' && !this.isValidHexCode( postfix ) ) {
        const titleCasePipe = new TitleCasePipe();
        postfix = titleCasePipe.transform( postfix );
      }
      navObj.title += ' ' + postfix;
    } );

    if ( !navObj.title ) {
      navObj.title = BRAND.name + ' - ' + BRAND.motto;
    } else if ( !navObj.title.includes( BRAND.name ) ) {
      // the home entry's title already starts with the brand; no "Codigrate - ... - Codigrate"
      navObj.title += ' - ' + BRAND.name;
    }

    if ( !navObj.keywords.some( ( k: string ) => k.toLowerCase() === BRAND.name.toLowerCase() ) ) {
      navObj.keywords.unshift( BRAND.name );
    }

    return navObj;
  };

  isValidHexCode( inputColorText: string ) {
    return /^([A-Fa-f0-9]{6})$/.test( inputColorText );
  }

  /**
   * Set or replace a <meta property="..."> tag (Open Graph etc).
   */
  private upsertMetaProperty( property: string, content: string ) {
    if ( !content ) {
      return;
    }
    const selector = `property='${ property }'`;
    if ( this.metaTagService.getTag( selector ) ) {
      this.metaTagService.updateTag( { property, content }, selector );
    } else {
      this.metaTagService.addTag( { property, content } );
    }
  }

  /**
   * Set or replace a <meta name="..."> tag (Twitter etc).
   */
  private upsertMetaName( name: string, content: string ) {
    if ( !content ) {
      return;
    }
    const selector = `name='${ name }'`;
    if ( this.metaTagService.getTag( selector ) ) {
      this.metaTagService.updateTag( { name, content }, selector );
    } else {
      this.metaTagService.addTag( { name, content } );
    }
  }

  /**
   * Replace the per-route BreadcrumbList <script type="application/ld+json">
   * so each page exposes its position in the hierarchy to crawlers.
   */
  private updateBreadcrumbJsonLd() {
    try {
      const path = this.doc?.location?.pathname || '/';
      const segments = path.split( '/' ).filter( s => s.length > 0 );

      const items: any[] = [ {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_ORIGIN + '/',
      } ];

      // The last crumb is the page itself: name it by its title (set just before this
      // runs) rather than its slug, so a post reads "Anatomy of a Dark Theme: Paris",
      // not "Anatomy Of The Paris Theme". Site suffixes after " | " are dropped.
      const pageTitle = ( this.doc?.title || '' ).split( ' | ' )[ 0 ].trim();
      let cumulative = '';
      segments.forEach( ( seg, idx ) => {
        cumulative += '/' + seg;
        const isLast = idx === segments.length - 1;
        items.push( {
          '@type': 'ListItem',
          position: idx + 2,
          name: isLast && segments.length > 1 && pageTitle ? pageTitle : this.humanize( seg ),
          item: SITE_ORIGIN + cumulative,
        } );
      } );

      const payload = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items,
      };

      const id = 'codigrate-breadcrumb-jsonld';
      let script = this.doc.getElementById( id ) as HTMLScriptElement | null;
      if ( !script ) {
        script = this.doc.createElement( 'script' );
        script.setAttribute( 'id', id );
        script.setAttribute( 'type', 'application/ld+json' );
        this.doc.head.appendChild( script );
      }
      script.textContent = JSON.stringify( payload );
    } catch ( e ) {
      // Non-fatal.
    }
  }

  private humanize( slug: string ): string {
    return slug
      .replace( /[-_]+/g, ' ' )
      .replace( /\b\w/g, c => c.toUpperCase() );
  }

  /**
   * The query params that make a page its own, per path: they survive into the canonical (alphabetical,
   * URL-encoded), everything else is dropped. Mirrors KEEP_PARAMS_PER_PATH in seo-route-meta.js, so the
   * canonical the client writes after bootstrap is the one the server rendered (before 2026-09-21 the
   * client collapsed every /theme?product=X onto the bare /theme).
   */
  private static readonly CANONICAL_PARAMS: { [ path: string ]: string[] } = {
    '/themes': [ 'platform', 'appearance', 'color', 'tag', 'category', 'edition', 'featured' ],
    '/theme': [ 'product', 'platform' ],
    '/theme/license': [ 'platform', 'product' ],
    '/theme/privacy': [ 'platform', 'product' ],
    '/palette': [ 'product' ],
    '/blog': [ 'category' ],
  };

  /** the canonical query string of the current location: the path's own params only, alphabetical, encoded like the server */
  private canonicalQuery( cleanPath: string ): string {
    const allowed = SeoService.CANONICAL_PARAMS[ cleanPath ];
    if ( !allowed ) {
      return '';
    }
    const search = new URLSearchParams( this.doc?.location?.search || '' );
    const keys: string[] = [];
    search.forEach( ( _value: string, key: string ) => { if ( keys.indexOf( key ) === -1 ) keys.push( key ); } );
    const kept: string[] = [];
    for ( const key of keys.sort() ) {
      let value = search.get( key );
      if ( allowed.indexOf( key.toLowerCase() ) === -1 || !value ) {
        continue;
      }
      // product keys and platform names are lowercase slugs, as on the server (buildCanonical)
      if ( key.toLowerCase() === 'product' || key.toLowerCase() === 'platform' ) {
        value = value.toLowerCase();
      }
      kept.push( `${ encodeURIComponent( key ) }=${ encodeURIComponent( value ) }` );
    }
    return kept.length > 0 ? '?' + kept.join( '&' ) : '';
  }

  /**
   * Keep <link rel="canonical"> + <meta property="og:url"> + <meta name="twitter:url">
   * in sync with the current route.
   */
  private updateCanonicalAndOgUrl() {
    try {
      const path = this.doc?.location?.pathname || '/';
      const cleanPath = path === '/' ? '/' : path.replace( /\/+$/, '' );
      const fullUrl = SITE_ORIGIN + cleanPath + this.canonicalQuery( cleanPath );

      const head = this.doc.head;
      let canonical = head.querySelector( "link[rel='canonical']" ) as HTMLLinkElement | null;
      if ( !canonical ) {
        canonical = this.doc.createElement( 'link' );
        canonical.setAttribute( 'rel', 'canonical' );
        head.appendChild( canonical );
      }
      canonical.setAttribute( 'href', fullUrl );

      this.upsertMetaProperty( 'og:url', fullUrl );
      this.upsertMetaName( 'twitter:url', fullUrl );
    } catch ( e ) {
      // Non-fatal: SSR or unusual environments may not expose location.
    }
  }
}
